import { theme } from '@contrast-security-inc/design-system-foundations'
import { css, cx } from '@emotion/css'
import React, { useMemo, useState } from 'react'
import { CsNextIcon, CsShowIcon } from '../../../../legacy-design-system/icons'
import { getSeverityData } from '../cell-content/vulnerabilities-cell'
import { getSeverityColor } from '../drilldown-panel/helpers'
import { mapVulnerabilityCLIToVulnerability } from '../cli-grid'
import { CvssScoringType } from '../../helpers/ui-interfaces'
import { Vulnerability } from '../../../../components/shared'
import { VulnerabilityModal } from '../../../../components/vulnerability-modal'
import { CveModal } from './cve-modal'
import { TreeType } from './interfaces'
import {
  renderChildVulnerableTooltip,
  renderVulnTooltip
} from './vulnerability-tooltips'
import { Organization } from '@contrast-security-inc/base-types'

const leafPadding = css`
  margin-left: -11px;
`

interface Props {
  treeType: TreeType
  data: {
    id: string
    isLeaf: boolean
    isOpenByDefault: boolean
    name: any
    vulnerabilities: any
    version: any
    hasVulnerableDependency: any
    searchString: string
    nestingLevel: any
    score: any
  }
  isOpen: boolean
  style: any
  setOpen: (state: boolean) => Promise<void>
  organization: Organization | undefined
}

export const getSeverityBadgeColor = (
  vulnerabilitySeverities,
  cvssScoringType: CvssScoringType
) => {
  let severityColor

  if (
    cvssScoringType === CvssScoringType.CVSS3_1 &&
    vulnerabilitySeverities.critical.total > 0
  ) {
    severityColor = getSeverityColor('CRITICAL')
  } else {
    severityColor =
      vulnerabilitySeverities.high.total > 0
        ? getSeverityColor('HIGH')
        : vulnerabilitySeverities.medium.total > 0
        ? getSeverityColor('MEDIUM')
        : getSeverityColor('LOW')
  }

  return severityColor
}

// Node component receives current node as a prop
export const TreeNode = (props: Props) => {
  const { data, isOpen, style, setOpen, organization, treeType } = props

  const {
    isLeaf,
    name,
    version,
    hasVulnerableDependency,
    nestingLevel,
    searchString
  } = data

  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isCVEModalOpen, setIsCVEModalOpen] = React.useState(false)
  const [modalVulnerability, setModalVulnerability] = useState<Vulnerability>()

  const vulnerabilities = useMemo(
    () =>
      treeType === 'CLI'
        ? data.vulnerabilities.map(mapVulnerabilityCLIToVulnerability)
        : data.vulnerabilities,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  )

  const cvssScoringType = useMemo(
    () =>
      organization
        ? (organization.cvssScoringType as CvssScoringType)
        : CvssScoringType.CVSS2,
    [organization]
  )

  const vulnerabilitySeverities = getSeverityData(
    vulnerabilities,
    cvssScoringType
  )

  const handleIsModalOpen = () => {
    setIsModalOpen(!isModalOpen)
    setIsCVEModalOpen(!isCVEModalOpen)
  }

  const handleModalDone = () => {
    setIsModalOpen(false)
    setIsCVEModalOpen(false)
  }

  const searchHighlight = css`
    background-color: ${theme.color.teal10};
  `
  const isSearchMatch = () => {
    if (searchString && name.toLowerCase().includes(searchString)) {
      return searchHighlight
    }
  }

  return (
    <div
      className="renderNode"
      data-e2e={`library-node-${name}`}
      style={{
        ...style,
        alignItems: 'center',
        display: 'flex',
        paddingLeft: nestingLevel * 30 + (isLeaf ? 48 : 0),
        height: '40px'
      }}
    >
      {!isLeaf &&
        (isOpen ? (
          <div
            className={css`
              display: inline - block;
              margin-left: 12px;
              color: ${theme.color.neutral100};
              cursor: pointer;
              margin-right: 5px;
            `}
          >
            <CsShowIcon
              size={20}
              onClick={() => setOpen(!isOpen)}
              data-testid={'click-arrow'}
            />
          </div>
        ) : (
          <div
            className={css`
              display: inline-block;
              margin-left: 12px;
              color: ${theme.color.neutral100};
              cursor: pointer;
              margin-right: 5px;
            `}
          >
            <CsNextIcon
              size={20}
              onClick={() => setOpen(!isOpen)}
              data-testid={'click-arrow'}
            />
          </div>
        ))}
      <div
        className={cx(isSearchMatch(), isLeaf && leafPadding)}
        data-testid={`tree-node-matching-search-${name}`}
      >
        <div
          className={css`
            display: flex;
            cursor: ${!isLeaf ? 'pointer' : 'auto'};
          `}
          onClick={() => setOpen(!isOpen)}
        >
          <div>{name}</div>
          <div
            className={css`
              margin-left: 12px;
            `}
          >
            {' '}
            {version ? `(${version})` : undefined}
          </div>
        </div>
      </div>
      <div
        onClick={() => setIsCVEModalOpen(true)}
        className={cx(css`
          margin-left: 6px;
          display: inline-block;
        `)}
      >
        {vulnerabilities.length > 0 &&
          renderVulnTooltip(
            vulnerabilities,
            vulnerabilitySeverities,
            cvssScoringType
          )}
        <div
          className={css`
            float: right;
            display: inline - flex;
          `}
        >
          {!isLeaf && hasVulnerableDependency && renderChildVulnerableTooltip()}
        </div>
      </div>
      {vulnerabilities.length > 0 && vulnerabilitySeverities && (
        <>
          <div
            className={css`
              height: 19px;
              width: 10px;
              border-left: 2px solid #bfbfbf;
              margin-left: 10px;
            `}
          />
          <div
            className={cx(css`
              display: inline-block;
              margin-left: 5px;
              cursor: pointer;
              color: ${theme.color.white};
              padding-left: 2px;
              padding-right: 3px;
              text-align: center;
              background: ${getSeverityBadgeColor(
                vulnerabilitySeverities,
                cvssScoringType
              )};
              border-radius: 50%;
              font-weight: 800;
              font-size: 12px;
              line-height: 19px;
              width: 19px;
            `)}
            onClick={() => setIsCVEModalOpen(true)}
          >
            <span data-e2e={'vulnerable-dependency-number'}>
              {vulnerabilities.length}
            </span>
          </div>

          {isCVEModalOpen && organization && (
            <CveModal
              isOpen={isCVEModalOpen}
              closeModal={() => setIsCVEModalOpen(false)}
              dependencyName={name}
              dependencyVersion={version}
              vulnerabilities={vulnerabilities}
              setModalVulnerability={setModalVulnerability}
              setLibraryVulnerabilityIsModalOpen={handleIsModalOpen}
              cvssScoringType={organization.cvssScoringType as CvssScoringType}
            />
          )}

          {isModalOpen && organization && modalVulnerability && (
            <VulnerabilityModal
              vulnerability={modalVulnerability}
              isOpen={isModalOpen}
              isCVEModalOpen={false}
              isUsedInDependencyTree={true}
              onBack={handleIsModalOpen}
              onCancel={handleModalDone}
              cvssScoringType={organization.cvssScoringType as CvssScoringType}
            />
          )}
        </>
      )}
    </div>
  )
}
